/**
 * Base fetch wrapper for AdminDash v2.
 *
 * Differs from v1's monolith `AdminAPI` class in three ways:
 *   1. It's a single ~80-line module — domain code lives in `api/<domain>.ts`
 *      files that call `request()` from here.
 *   2. Returns parsed JSON typed via the caller's generic; throws ApiError
 *      with shape { status, code?, message } so call sites can match on
 *      `code` (e.g., `UNAUTHORIZED`).
 *   3. Auth + silent token refresh handled in one place. JWT comes from
 *      the auth store; token refresh is captured from `x-token-refresh`
 *      response header without pestering the caller.
 */

import { useAuthStore } from '@/store/auth'

export class ApiError extends Error {
  status: number
  code?: string
  body?: unknown

  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

const BASE_URL =
  import.meta.env.VITE_ADMIN_API_BASE ?? 'https://srv.allenhark.com'

/** Legacy admin key for endpoints not yet migrated to JWT auth
 * (/api/customers, /api/vps/*, etc.). Read from Vite env at build time
 * and optionally overridden at runtime via sessionStorage — the
 * operator can paste a key into the Settings page without rebuilding. */
const ENV_ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY as string | undefined

function currentAdminKey(): string | undefined {
  if (typeof window !== 'undefined') {
    const runtime = window.sessionStorage.getItem('admin_key')
    if (runtime) return runtime
  }
  return ENV_ADMIN_KEY || undefined
}

export function setAdminKey(key: string | null): void {
  if (typeof window === 'undefined') return
  if (!key) window.sessionStorage.removeItem('admin_key')
  else window.sessionStorage.setItem('admin_key', key)
}

export function hasAdminKey(): boolean {
  return !!currentAdminKey()
}

/** Tracks whether we're already attempting a token refresh to avoid
 *  concurrent refresh races from parallel 401s. */
let refreshInFlight: Promise<string | null> | null = null

/** Refresh couldn't be attempted (network or server error): keep the session. */
const REFRESH_UNAVAILABLE = '__refresh_unavailable__'

/** Attempt to refresh the JWT via /api/auth/refresh. Returns the new
 *  token on success, null on failure (expired refresh window, etc.). */
async function tryRefreshToken(): Promise<string | null> {
  const token = useAuthStore.getState().token
  if (!token) return null

  try {
    const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
    if (!res.ok) {
      // Only the server saying the session is gone ends it. A 5xx or a
      // gateway hiccup is not a reason to sign the operator out.
      if (res.status === 401 || res.status === 403) return null
      return REFRESH_UNAVAILABLE
    }
    const data = await res.json()
    const fresh = data.jwt ?? data.token ?? null
    if (fresh) {
      useAuthStore.getState().setToken(fresh)
    }
    return fresh
  } catch {
    // Offline, DNS, or a dropped connection: keep the session.
    return REFRESH_UNAVAILABLE
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Skip auth header. Default: attach JWT (or admin key for legacy callers). */
  noAuth?: boolean
  /** Per-request timeout (ms). Default: 30s. */
  timeoutMs?: number
  /** Internal flag — prevents infinite retry loops. */
  _retried?: boolean
}

export async function request<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  // 30s matches the upstream Cherry API timeout the admin backend
  // uses; a shorter client timeout would abort VPS / on-chain
  // requests that are still legitimately in flight. Use per-call
  // override for endpoints known to fail fast.
  const { body, noAuth, timeoutMs = 30_000, headers, _retried, ...rest } = opts

  const reqHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined),
  }
  if (body !== undefined && !(body instanceof FormData)) {
    reqHeaders['Content-Type'] = 'application/json'
  }
  if (!noAuth) {
    const token = useAuthStore.getState().token
    if (token) reqHeaders['Authorization'] = `Bearer ${token}`
    const adminKey = currentAdminKey()
    if (adminKey) reqHeaders['x-admin-key'] = adminKey
  }

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)

  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: reqHeaders,
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
      signal: ctrl.signal,
    })
  } finally {
    clearTimeout(timer)
  }

  // Silent token refresh — admin_service emits a fresh JWT in this header
  // once the current one is past its halfway mark. Caller never sees it.
  const refreshed = res.headers.get('x-token-refresh')
  if (refreshed) {
    useAuthStore.getState().setToken(refreshed)
  }

  // 401 handling — attempt token refresh + retry once. If that also
  // fails, log the user out so they don't sit on a broken dashboard.
  if (res.status === 401 && !noAuth && !_retried) {
    // Deduplicate concurrent refresh attempts from parallel requests
    if (!refreshInFlight) {
      refreshInFlight = tryRefreshToken().finally(() => {
        refreshInFlight = null
      })
    }
    const freshToken = await refreshInFlight

    if (freshToken === REFRESH_UNAVAILABLE) {
      // Couldn't reach the refresh endpoint. Don't sign out over a network
      // blip; surface this request's 401 to the caller instead.
    } else if (freshToken) {
      // Retry the original request with the new token
      return request<T>(path, { ...opts, _retried: true })
    } else {
      // Refresh refused — session is truly expired. Log out.
      useAuthStore.getState().logout()
      // Redirect to login if we're in a browser context
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
  }

  let data: unknown = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!res.ok) {
    const errBody = data as { error?: string; message?: string; code?: string } | null
    const message =
      errBody?.error ?? errBody?.message ?? `HTTP ${res.status} ${res.statusText}`
    throw new ApiError(res.status, message, errBody?.code, data)
  }

  return data as T
}

export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    usp.set(k, String(v))
  }
  const s = usp.toString()
  return s ? `?${s}` : ''
}

export const API_BASE_URL = BASE_URL
